import { z } from "zod";
export const RETENTION_DOMAINS = [
    "chat",
    "vault",
    "email_tracking",
    "email_capture",
    "web_push",
    "audit",
    "ai",
];
export const RETENTION_MODES = ["tombstone", "hard_delete", "anonymize"];
export const RetentionDomainSchema = z.enum(RETENTION_DOMAINS);
export const RetentionModeSchema = z.enum(RETENTION_MODES);
/** Global floor so a fat-fingered daysToKeep can't nuke recent data. */
export const MIN_DAYS_TO_KEEP = 30;
/** Audit domain keeps a much higher floor (UK 6yr limitation). Enforced server-side per-domain. */
export const AUDIT_MIN_DAYS_TO_KEEP = 2190;
/**
 * SSOT for which modes are valid per domain.
 * Used by the admin card's mode dropdown so only domain-legal choices
 * are offered; avoids the UI sending a mode the server will reject.
 *
 * Keyed on every RETENTION_DOMAINS value so TypeScript catches any new
 * domain added to the constant without a corresponding modes entry.
 *
 * Must be defined before the input schemas so the .superRefine can
 * reference it at module evaluation time.
 */
export const RETENTION_DOMAIN_MODES = {
    chat: ["tombstone"],
    vault: ["hard_delete"],
    email_tracking: ["hard_delete"],
    email_capture: ["anonymize"],
    web_push: ["hard_delete"],
    audit: ["hard_delete"],
    ai: ["hard_delete"],
};
/** Strict on INPUT (admin PUT). */
export const RetentionPolicyUpdateSchema = z
    .object({
    domain: RetentionDomainSchema,
    enabled: z.boolean(),
    daysToKeep: z.number().int().min(MIN_DAYS_TO_KEEP).max(36500),
    mode: RetentionModeSchema,
})
    .superRefine((val, ctx) => {
    const allowed = RETENTION_DOMAIN_MODES[val.domain];
    if (allowed && !allowed.includes(val.mode)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["mode"],
            message: `mode '${val.mode}' is not supported for domain '${val.domain}' (allowed: ${allowed.join(", ")})`,
        });
    }
});
/** LOOSE on OUTPUT (response list) so a legacy/odd row can't break the admin table. */
export const RetentionPolicySchema = z.object({
    tenantSlug: z.string(),
    domain: z.string(),
    enabled: z.boolean(),
    daysToKeep: z.number().int(),
    mode: z.string(),
    updatedBy: z.string(),
    updatedAt: z.string(),
});
export const RetentionRunSummarySchema = z.object({
    id: z.string(),
    startedAt: z.string(),
    finishedAt: z.string().nullable(),
    dryRun: z.boolean(),
    status: z.string(),
    summary: z.unknown(),
});
export const RetentionPreviewSchema = z.object({
    domain: z.string(),
    count: z.number().int(),
});
export const RetentionPolicyUpsertSchema = RetentionPolicyUpdateSchema.and(z.object({
    tenantSlug: z.string().min(1).max(64),
}));
export const RetentionPoliciesResponseSchema = z.object({
    policies: z.array(RetentionPolicySchema),
});
export const RetentionRunsResponseSchema = z.object({
    runs: z.array(RetentionRunSummarySchema),
});
/** Response schema for PUT /admin/retention/policies → { ok: true }. */
export const RetentionMutationResponseSchema = z.object({ ok: z.boolean() });
//# sourceMappingURL=retention.js.map